// AuditLog — Global event search
import { useMemo, useState } from "react";
import type { CSSProperties } from "react";

type Ev = { id: string; at: string; actor: string; action: string; target: string; diff?: Record<string, [unknown, unknown]> };

const ACTORS = ["olivia.t", "marcus.j", "priya.s", "system", "ci-bot"];
const ACTIONS = ["record.create", "record.update", "record.delete", "approval.approve", "approval.reject", "key.created", "rule.run"];

function makeMock(): Ev[] {
  const out: Ev[] = [];
  for (let i = 0; i < 80; i++) {
    const a = ACTIONS[i % ACTIONS.length];
    const id = `evt_${(94000 + i).toString(36)}`;
    out.push({
      id,
      at: `2026-05-${(15 - Math.floor(i / 10)).toString().padStart(2, "0")} ${(23 - (i % 24)).toString().padStart(2, "0")}:${(i * 7 % 60).toString().padStart(2, "0")}:00`,
      actor: ACTORS[i % ACTORS.length],
      action: a,
      target: a.startsWith("record") ? `instruments/${["AAPL", "MSFT", "TSLA", "NVDA"][i % 4]}` : a.startsWith("approval") ? `approval ap_${i}` : "system",
      diff: a === "record.update" ? { status: ["draft", "live"], currency: ["USD", "EUR"] } : undefined,
    });
  }
  return out;
}

const ALL = makeMock();

export function AuditLog() {
  const [actor, setActor] = useState("");
  const [action, setAction] = useState("");
  const [q, setQ] = useState("");
  const [expanded, setExpanded] = useState<string | null>(null);

  const filtered = useMemo(() => ALL.filter(e =>
    (!actor || e.actor === actor) &&
    (!action || e.action === action) &&
    (!q || e.target.includes(q) || e.id.includes(q))
  ), [actor, action, q]);

  const today = filtered.filter(e => e.at.startsWith("2026-05-15")).length;
  const actorsCount = new Set(filtered.map(e => e.actor)).size;
  const topAction = Object.entries(filtered.reduce((m, e) => { m[e.action] = (m[e.action] || 0) + 1; return m; }, {} as Record<string, number>)).sort((a, b) => b[1] - a[1])[0]?.[0] ?? "—";

  const exportData = (fmt: "csv" | "json") => {
    let content: string;
    if (fmt === "json") content = JSON.stringify(filtered, null, 2);
    else {
      const head = "id,at,actor,action,target\n";
      content = head + filtered.map(e => `${e.id},${e.at},${e.actor},${e.action},${e.target}`).join("\n");
    }
    const blob = new Blob([content], { type: fmt === "json" ? "application/json" : "text/csv" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `audit.${fmt}`; a.click();
  };

  return (
    <div style={{ padding: 16, display: "grid", gap: 16 }}>
      <header style={{ ...panel, padding: 20 }}>
        <div style={eyebrow}>HISTORY</div>
        <div style={{ font: "var(--t-headline-md)", fontStyle: "italic", fontWeight: 400 }}>Audit log</div>
      </header>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 }}>
        <Stat label="Events today" value={today.toLocaleString()} />
        <Stat label="Unique actors" value={actorsCount.toString()} />
        <Stat label="Top action" value={topAction} mono />
      </div>

      <div style={{ ...panel, padding: 14, display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search by target or id…" style={{ ...input, minWidth: 260, flex: 1 }} />
        <select aria-label="Filter by actor" value={actor} onChange={e => setActor(e.target.value)} style={input}>
          <option value="">All actors</option>
          {ACTORS.map(a => <option key={a}>{a}</option>)}
        </select>
        <select aria-label="Filter by action" value={action} onChange={e => setAction(e.target.value)} style={input}>
          <option value="">All actions</option>
          {ACTIONS.map(a => <option key={a}>{a}</option>)}
        </select>
        <button onClick={() => exportData("csv")} style={pillBtn}>Export CSV</button>
        <button onClick={() => exportData("json")} style={pillBtn}>Export JSON</button>
      </div>

      <div style={{ ...panel, padding: 0, maxHeight: "60vh", overflow: "auto" }} className="scroll">
        {filtered.slice(0, 200).map((e, i) => (
          <div key={e.id} style={{ borderTop: i ? "1px solid var(--md-outline-var)" : "none" }}>
            <button onClick={() => setExpanded(expanded === e.id ? null : e.id)} style={{
              width: "100%", textAlign: "left", display: "grid", gridTemplateColumns: "150px 110px 1fr 1fr 24px", gap: 12,
              padding: "10px 16px", background: "transparent", border: "none", cursor: "pointer",
              alignItems: "center", color: "var(--md-on-surface)",
            }}>
              <span style={{ font: "var(--t-mono-sm)", color: "var(--md-on-surface-var)" }}>{e.at}</span>
              <span style={{ font: "var(--t-mono-sm)" }}>{e.actor}</span>
              <span style={{ font: "var(--t-label-md)", color: "var(--md-tertiary)" }}>{e.action}</span>
              <span style={{ font: "var(--t-mono-sm)" }}>{e.target}</span>
              <span>{expanded === e.id ? "▾" : "▸"}</span>
            </button>
            {expanded === e.id && (
              <pre style={{
                margin: 0, padding: "12px 24px", background: "var(--md-sc-low)",
                font: "var(--t-mono-sm)", whiteSpace: "pre-wrap",
              }}>{JSON.stringify(e, null, 2)}</pre>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function Stat({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div style={{ ...panel, padding: 16 }}>
      <div style={eyebrow}>{label.toUpperCase()}</div>
      <div style={{ font: mono ? "var(--t-headline-sm)" : "var(--t-display-sm)", marginTop: 6, fontStyle: mono ? "normal" : "italic", fontWeight: 400, fontFamily: mono ? "var(--font-mono)" : undefined }} className="tabular">
        {value}
      </div>
    </div>
  );
}

const panel: CSSProperties = { background: "var(--md-sc-lowest)", border: "1px solid var(--md-outline-var)", borderRadius: "var(--r-lg)" };
const eyebrow: CSSProperties = { font: "var(--t-label-sm)", color: "var(--md-tertiary)", letterSpacing: "0.12em" };
const input: CSSProperties = { padding: "8px 12px", borderRadius: "var(--r-pill)", border: "1px solid var(--md-outline-var)", background: "var(--md-sc-low)", color: "var(--md-on-surface)", font: "var(--t-body-md)" };
const pillBtn: CSSProperties = { padding: "8px 16px", borderRadius: "var(--r-pill)", border: "1px solid var(--md-outline-var)", background: "var(--md-sc-lowest)", color: "var(--md-on-surface)", cursor: "pointer", font: "var(--t-label-md)" };

export default AuditLog;
